import { useState, useRef, useCallback } from 'react';
import { useDrag } from 'react-dnd';
import { cn } from '@/app/components/ui/utils';
import { OverrideMarker } from '@/components/crew/override-marker';
import { useIsTouchDevice } from '@/hooks/use-touch-device';
import type { BoardTech, BoardRecentAssignment } from '@/hooks/use-staffing-board';
import { formatMoney, OPTIONAL_FRACTION_DIGITS } from '@/lib/currency';
import { formatDateOnly, formatLocalDate } from '@/lib/date-utils';
import {
  Award,
  Wrench,
  Zap,
  Anchor,
  Shield,
  User,
  Package,
  Clock,
  Check,
  CalendarCheck,
  Timer,
  FolderKanban,
  Inbox,
  Mail,
  Eye,
  CheckCircle2,
  Plane,
  ExternalLink,
} from 'lucide-react';

export const TECH_CARD_TYPE = 'TECH_CARD';

const CERT_LEVEL_COLORS: Record<number, string> = {
  3: 'border-l-purple-600 bg-purple-50',
  2: 'border-l-blue-600 bg-blue-50',
  1: 'border-l-green-600 bg-green-50',
  0: 'border-l-gray-300 bg-white',
};

const CERT_LEVEL_LABELS: Record<number, string> = {
  3: 'Level 3',
  2: 'Level 2',
  1: 'Level 1',
  0: '',
};

const AVAILABILITY_LABELS: Record<string, string> = {
  unavailable: 'Unavailable',
};

const OFFBOARDED_LABELS: Record<string, string> = {
  terminated: 'Terminated',
  left: 'Left',
};

const SKILL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  rigging: Anchor,
  welding: Zap,
  rescue: Shield,
  ndt: Wrench,
};

function parseLocalDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function computeMiniStats(
  assignments: BoardRecentAssignment[],
  todayValue = formatLocalDate(new Date())
) {
  if (assignments.length === 0) return null;

  const today = parseLocalDate(todayValue);

  let lastDispatched: Date | null = null;
  let daysOnCurrent: number | null = null;
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const startOfNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const projectsThisMonthSet = new Set<string>();

  for (const a of assignments) {
    if (a.end_date) {
      const d = parseLocalDate(a.end_date);
      if (!lastDispatched || d > lastDispatched) lastDispatched = d;
    }

    if (!a.end_date && a.start_date) {
      const start = parseLocalDate(a.start_date);
      const days = Math.round((today.getTime() - start.getTime()) / 86_400_000);
      if (days >= 0 && (daysOnCurrent === null || days > daysOnCurrent)) daysOnCurrent = days;
    }

    if (a.start_date) {
      const start = parseLocalDate(a.start_date);
      if (start >= startOfMonth && start < startOfNextMonth) {
        projectsThisMonthSet.add(a.project_name);
      }
    }
  }

  return {
    lastDispatched,
    daysOnCurrent,
    projectsThisMonth: projectsThisMonthSet.size,
  };
}

function formatDate(d: Date): string {
  return formatDateOnly(formatLocalDate(d), '—', { month: 'short', day: 'numeric' });
}

function getSkillIcon(skillName: string) {
  const key = skillName.toLowerCase();
  for (const [keyword, Icon] of Object.entries(SKILL_ICONS)) {
    if (key.includes(keyword)) return Icon;
  }
  return Award;
}

const TIMESHEET_STATUS_COLORS: Record<string, string> = {
  approved: 'text-green-500',
  submitted: 'text-yellow-500',
  rejected: 'text-red-500',
  draft: 'text-gray-400',
};

const PACKET_STATUS_CONFIG: Record<string, { label: string; color: string; Icon: React.ComponentType<{ className?: string }> }> = {
  acknowledged: { label: 'Confirmed', color: 'bg-green-100 text-green-700', Icon: CheckCircle2 },
  viewed: { label: 'Viewed', color: 'bg-blue-100 text-blue-700', Icon: Eye },
  sent: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', Icon: Mail },
};

const TRAVEL_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  confirmed: { label: 'Travel OK', color: 'bg-green-100 text-green-700' },
  pending: { label: 'Travel pending', color: 'bg-yellow-100 text-yellow-700' },
  cancelled: { label: 'Travel cancelled', color: 'bg-red-100 text-red-700' },
};

interface TechCardProps {
  tech: BoardTech;
  assignmentId?: number;
  overrideReason?: string;
  sourceProjectId?: number;
  sourceJobId?: number;
  canDrag: boolean;
  canEdit?: boolean;
  crewEnabled: boolean;
  inventoryEnabled: boolean;
  payrollEnabled: boolean;
  showFinancials: boolean;
  currency: string;
  isSelected?: boolean;
  onSelectToggle?: (techId: number, shiftKey: boolean) => void;
  isJustDropped?: boolean;
  onDropAnimationEnd?: (techId: number) => void;
  highlightExpiredCerts?: boolean;
  onTouchAssign?: (techId: number, techName: string, assignmentId?: number) => void;
  packetStatus?: 'sent' | 'viewed' | 'acknowledged';
  jobColors?: Map<number, string>;
  placements?: number[];
  onBadgeClick?: (jobId: number, event?: React.MouseEvent) => void;
  orgToday?: string;
}

export function TechCard({ tech, assignmentId, overrideReason, sourceProjectId, sourceJobId, canDrag, canEdit, crewEnabled, inventoryEnabled, payrollEnabled, showFinancials, currency, isSelected, onSelectToggle, isJustDropped, onDropAnimationEnd, highlightExpiredCerts, onTouchAssign, packetStatus, jobColors, placements, onBadgeClick, orgToday }: TechCardProps) {
  const [flipped, setFlipped] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isTouchDevice = useIsTouchDevice();

  const effectiveCanDrag = canDrag && !flipped;

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: TECH_CARD_TYPE,
      item: {
        techId: tech.id,
        personId: tech.person_id,
        assignmentId,
        sourceProjectId,
        sourceJobId,
      },
      canDrag: effectiveCanDrag,
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [tech.id, tech.person_id, assignmentId, sourceProjectId, sourceJobId, effectiveCanDrag],
  );

  const handleClick = (e: React.MouseEvent) => {
    if (onSelectToggle && e.shiftKey) {
      e.preventDefault();
      onSelectToggle(tech.id, true);
      return;
    }
    setFlipped((f) => !f);
  };

  // eslint-disable-next-line react-hooks/refs
  drag(ref);

  const handleAnimationEnd = useCallback(() => {
    onDropAnimationEnd?.(tech.id);
  }, [onDropAnimationEnd, tech.id]);

  const certLevel = tech.highest_cert_level ?? 0;
  const hasExpired = tech.has_expired_certs ?? false;
  const colorClass = CERT_LEVEL_COLORS[certLevel] ?? CERT_LEVEL_COLORS[0];
  const isUnavailable =
    crewEnabled && tech.availability_status === 'unavailable';
  const checkedIn = crewEnabled && tech.checked_in_today === true;
  const isPlaced = sourceProjectId != null || sourceJobId != null;
  const isPlacedPendingDispatch = isPlaced && !packetStatus;
  const showJobDots = placements != null && placements.length >= 2 && jobColors != null;

  return (
    <div
      ref={ref}
      onClick={handleClick}
      onAnimationEnd={isJustDropped ? handleAnimationEnd : undefined}
      className={cn(
        'relative w-full rounded-lg border-l-4 shadow-sm transition-all select-none',
        'hover:shadow-md cursor-pointer min-h-[48px]',
        'print:shadow-none print:border-l-2 print:rounded-md print:cursor-default',
        'print:border-gray-400 print:bg-white print:break-inside-avoid',
        colorClass,
        hasExpired && crewEnabled && 'ring-2 ring-red-500',
        highlightExpiredCerts && hasExpired && 'ring-2 ring-red-500 bg-red-50 shadow-md scale-[1.02]',
        highlightExpiredCerts && !hasExpired && 'opacity-40',
        isSelected && 'outline outline-2 outline-blue-500 outline-offset-1',
        isDragging && 'opacity-40 rotate-2 scale-95',
        effectiveCanDrag && 'cursor-grab active:cursor-grabbing',
        isUnavailable && 'opacity-50',
        isPlacedPendingDispatch && 'after:absolute after:inset-0 after:rounded-lg after:border after:border-dashed after:border-gray-500 after:pointer-events-none',
      )}
      style={{
        perspective: '600px',
        ...(isJustDropped ? { animation: 'dropSettle 150ms ease-out forwards' } : {}),
        ...(flipped ? { touchAction: 'auto' } : {}),
      }}
      data-print-tech-card
      data-placement-state={isPlaced ? (isPlacedPendingDispatch ? 'placed' : 'dispatched') : 'bench'}
    >
      {/* Multi-select checkmark overlay */}
      {isSelected && (
        <span className="absolute top-1.5 left-1.5 h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center z-10 print:hidden">
          <Check className="h-3 w-3 text-white" />
        </span>
      )}
      {/* Availability overlay */}
      {isUnavailable && (
        <div className="absolute inset-0 rounded-lg bg-gray-400/30 z-[1] pointer-events-none flex items-end justify-center pb-1 print:hidden">
          <span className="text-[10px] font-semibold text-gray-700 bg-gray-200/90 px-1.5 py-0.5 rounded">
            {AVAILABILITY_LABELS[tech.availability_status!] ?? tech.availability_status}
          </span>
        </div>
      )}
      <div
        className={cn(
          'transition-transform duration-300',
          'print:[transform:none]',
          flipped && '[transform:rotateY(180deg)]',
        )}
        style={{ transformStyle: 'preserve-3d' }}
      >
        {/* Front */}
        <div
          className={cn(
            'p-3 print:p-1.5 print:visible',
            flipped && 'absolute inset-0 invisible',
          )}
        >
          <div className="flex items-center gap-2">
            <p className="min-w-0 flex-1 truncate text-sm font-semibold text-black print:text-xs">
              {tech.first_name} {tech.last_name}
            </p>
            {OFFBOARDED_LABELS[tech.status] && (
              <span
                data-testid="offboarded-label"
                className="shrink-0 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-700"
              >
                {OFFBOARDED_LABELS[tech.status]}
              </span>
            )}
            {overrideReason && <OverrideMarker reason={overrideReason} />}
          </div>

          {(crewEnabled || showJobDots) && (
            <div className="mt-2 flex min-h-5 items-end justify-between gap-2 print:mt-0.5">
              {crewEnabled && (
                <div className="flex items-center gap-1.5 flex-wrap print:gap-1">
                  {tech.skills?.slice(0, 4).map((skill) => {
                    const Icon = getSkillIcon(skill.skill_name);
                    return (
                      <span
                        key={skill.id}
                        title={skill.skill_name}
                        className="inline-flex items-center justify-center h-5 w-5 rounded bg-gray-100 text-gray-600 print:hidden"
                      >
                        <Icon className="h-3 w-3" />
                      </span>
                    );
                  })}
                  <span className="hidden print:inline text-[10px] text-gray-600">
                    {tech.skills?.slice(0, 4).map((s) => s.skill_name).join(', ')}
                  </span>
                </div>
              )}
              {showJobDots && (
                <div className="flex flex-wrap items-center justify-end gap-1 print:hidden">
                  {placements.map((jobId) => {
                    const color = jobColors.get(jobId) ?? '#9ca3af';
                    return (
                      <button
                        key={jobId}
                        type="button"
                        disabled={!canEdit}
                        title={canEdit ? 'Remove from this job' : 'Assigned to this job'}
                        onClick={(e) => {
                          e.stopPropagation();
                          onBadgeClick?.(jobId, e);
                        }}
                        className={cn(
                          'h-4 w-4 rounded-full border border-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500',
                          canEdit ? 'cursor-pointer hover:opacity-80' : 'cursor-default opacity-80',
                        )}
                        style={{ backgroundColor: color }}
                        data-testid="job-badge"
                        data-job-id={jobId}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Back (details) — hidden in print */}
        {flipped && (
          <div
            className="max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white p-3"
            style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}
          >
            <div className="flex items-start gap-2 mb-2">
              {tech.photo ? (
                <img
                  src={tech.photo}
                  alt={`${tech.first_name} ${tech.last_name}`}
                  className="h-8 w-8 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-gray-500" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-semibold text-black">
                  {tech.first_name} {tech.last_name}
                </p>
                {tech.job_title && <p className="text-xs text-gray-500">{tech.job_title}</p>}
                {crewEnabled && certLevel > 0 && (
                  <p className={cn('text-xs font-medium', hasExpired ? 'text-red-600' : 'text-gray-600')}>
                    IRATA {CERT_LEVEL_LABELS[certLevel]}{hasExpired ? ' — expired' : ''}
                  </p>
                )}
              </div>
            </div>

            {tech.tech_id != null && (
              <a
                href={`/crew/techs/${tech.tech_id}`}
                className="mb-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                onClick={(event) => event.stopPropagation()}
                data-testid="crew-profile-link"
              >
                View H3Crew profile
                <ExternalLink className="h-3 w-3" />
              </a>
            )}

            <div className="mb-2 flex flex-wrap gap-1">
              {showFinancials && tech.daily_rate && (
                <span className="inline-flex items-center rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700">
                  {formatMoney(tech.daily_rate, currency, OPTIONAL_FRACTION_DIGITS)}/day
                </span>
              )}
              {payrollEnabled && tech.timesheet_status && (
                <span className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                  <Clock className={cn('h-3 w-3', TIMESHEET_STATUS_COLORS[tech.timesheet_status] ?? 'text-gray-400')} />
                  Timesheet: {tech.timesheet_status}
                </span>
              )}
              {checkedIn && (
                <span className="inline-flex items-center gap-1 rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-700">
                  <Check className="h-3 w-3" />
                  Checked in
                </span>
              )}
              {tech.availability_status && (
                <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-700">
                  Availability: {AVAILABILITY_LABELS[tech.availability_status] ?? tech.availability_status}
                </span>
              )}
            </div>

            {crewEnabled && isPlaced && (
              <div className="mb-2">
                {packetStatus ? (() => {
                  const config = PACKET_STATUS_CONFIG[packetStatus];
                  if (!config) return null;
                  const { label, color, Icon: PacketIcon } = config;
                  return (
                    <span className={cn('inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded', color)} data-testid="packet-status-badge">
                      <PacketIcon className="h-3 w-3" />
                      Dispatch packet: {label}
                    </span>
                  );
                })() : (
                  <span className="inline-flex items-center gap-1 rounded border border-dashed border-gray-400 bg-gray-50 px-1.5 py-0.5 text-xs text-gray-600" data-testid="packet-status-badge">
                    Placed — not dispatched
                  </span>
                )}
              </div>
            )}

            {crewEnabled && tech.travel_status && (() => {
              const config = TRAVEL_STATUS_CONFIG[tech.travel_status];
              if (!config) return null;
              return (
                <div className="mb-2" data-testid="travel-status-badge">
                  <span className={cn('inline-flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded', config.color)}>
                    <Plane className="h-3 w-3" />
                    {config.label}
                  </span>
                </div>
              );
            })()}

            {crewEnabled && tech.certifications && tech.certifications.length > 0 && (
              <div className="mb-2">
                <p className="text-xs font-medium text-gray-700 mb-1">Certifications</p>
                <ul className="space-y-0.5">
                  {tech.certifications.map((c) => (
                    <li
                      key={c.id}
                      className={cn(
                        'text-xs',
                        c.status === 'expired' ? 'text-red-600 font-medium' : 'text-gray-600',
                      )}
                    >
                      {c.cert_name || c.cert_type}
                      {c.expiry_date && ` — ${formatDateOnly(c.expiry_date)}`}
                      {c.status === 'expired' && ' (EXPIRED)'}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {crewEnabled && tech.skills && tech.skills.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-700 mb-1">Skills</p>
                <div className="flex flex-wrap gap-1">
                  {tech.skills.map((s) => (
                    <span
                      key={s.id}
                      className="inline-block text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded"
                    >
                      {s.skill_name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {crewEnabled &&
              tech.recent_assignments &&
              tech.recent_assignments.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs font-medium text-gray-700 mb-1">
                    Recent Projects
                  </p>
                  <ul className="space-y-0.5">
                    {tech.recent_assignments.map((a, idx) => (
                      <li key={idx} className="text-xs text-gray-600">
                        <span className="font-medium">{a.project_name}</span>
                        {a.start_date && a.end_date && (
                          <span className="text-gray-400 ml-1">
                            {formatDateOnly(a.start_date)} – {formatDateOnly(a.end_date)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {/* Recent activity mini-stats (F08) */}
            {crewEnabled && (() => {
              const assignments = tech.recent_assignments ?? [];
              if (assignments.length === 0) {
                return (
                  <div className="mt-2" data-testid="mini-stats-empty">
                    <p className="text-xs font-medium text-gray-700 mb-1">Recent activity</p>
                    <p className="text-xs text-gray-400 italic flex items-center gap-1">
                      <Inbox className="h-3 w-3" />
                      No recent activity
                    </p>
                  </div>
                );
              }
              const stats = computeMiniStats(
                assignments,
                orgToday ?? formatLocalDate(new Date())
              );
              if (!stats) return null;
              return (
                <div className="mt-2" data-testid="mini-stats">
                  <p className="text-xs font-medium text-gray-700 mb-1">Recent activity</p>
                  <ul className="space-y-0.5">
                    <li className="text-xs text-gray-600 flex items-center gap-1">
                      <CalendarCheck className="h-3 w-3 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-500">Last dispatched:</span>
                      <span className="font-medium">
                        {stats.lastDispatched ? formatDate(stats.lastDispatched) : '—'}
                      </span>
                    </li>
                    <li className="text-xs text-gray-600 flex items-center gap-1">
                      <Timer className="h-3 w-3 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-500">Days on current:</span>
                      <span className="font-medium">
                        {stats.daysOnCurrent !== null ? stats.daysOnCurrent : '—'}
                      </span>
                    </li>
                    <li className="text-xs text-gray-600 flex items-center gap-1">
                      <FolderKanban className="h-3 w-3 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-500">Projects this month:</span>
                      <span className="font-medium">{stats.projectsThisMonth}</span>
                    </li>
                  </ul>
                </div>
              );
            })()}

            {inventoryEnabled && (tech.kit_count ?? 0) > 0 && (
              <div className="mt-2">
                <p className="text-xs font-medium text-gray-700 mb-1">Equipment</p>
                <span className="inline-flex items-center gap-1 text-xs bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">
                  <Package className="h-3 w-3" />
                  {tech.kit_count} kit{tech.kit_count !== 1 ? 's' : ''} assigned
                </span>
              </div>
            )}

            <p className="mt-2 text-[10px] text-gray-400 italic">Tap to flip back</p>

            {/* Touch-safe assign button */}
            {isTouchDevice && canDrag && onTouchAssign && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTouchAssign(tech.id, `${tech.first_name} ${tech.last_name}`, assignmentId);
                }}
                className="mt-2 w-full rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 active:bg-blue-800 transition-colors"
                data-testid={`touch-assign-btn-${tech.id}`}
              >
                {assignmentId ? 'Move to…' : 'Assign to…'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
